/**
 * 供需中心相关 API
 */
import request from '@/utils/request'
import baseUrl from '@/utils/baseUrl'

const supplyDemandApi = {}

/**
 * 字典查询
 * @param {Array} dictCodeList 字典编码数组
 * @returns {Promise} 返回字典数据
 * 
 * 字典类型说明：
 * - 成果类别：yAchievementType
 * - 领域：yField
 * - 人才类型：dictExpertTypeFirstList
 * - 省市区：fjkjtarea
 */
supplyDemandApi.getDictData = function(dictCodeList = []) {
  return request({
    url: '/dict-ext/showDataDict',
    method: 'post',
    data: {
      dictCodeList: dictCodeList
    },
    baseURL: baseUrl.dict,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 获取图片地址
 * @param {String} fileId 文件ID
 * @returns {String} 图片访问地址
 */
supplyDemandApi.getImageUrl = function(fileId) {
  if (!fileId) {
    return ''
  }
  return `${baseUrl.oss}/file/download/${fileId}`
}

/**
 * 成果超市接口
 * @param {Object} params 查询参数
 * @param {String} params.firstCategory 成果一级类别
 * @param {String} params.secondCategory 成果二级类别
 * @param {String} params.field 产业领域
 * @param {Number} params.num 条数
 * @returns {Promise} 返回成果列表数据
 */
supplyDemandApi.getAchievementMarket = function(params = {}) {
  const query = {
    field: params.field || '',
    num: params.num || 10
  }
  if (params.firstCategory) query.firstCategory = params.firstCategory
  if (params.secondCategory) query.secondCategory = params.secondCategory
  return request({
    url: '/open/achievement/market',
    method: 'get',
    params: query,
    baseURL: baseUrl.achievement,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 成果超市-分页列表接口
 * @param {Object} params 查询参数
 * @param {String} params.firstCategory 成果一级类别
 * @param {String} params.secondCategory 成果二级类别
 * @param {String} params.field 产业领域
 * @param {String} params.techMaturity 技术成熟度
 * @param {String} params.transformMethod 转化方式
 * @param {String} [params.createById] 成果创建人ID
 * @param {Number} params.page 页数
 * @param {Number} params.pageSize 条数
 * @returns {Promise} 返回成果分页列表
 */
supplyDemandApi.getAchievementSupermarketList = function(params = {}) {
  const pageNum = params.pageNum || params.page || 1
  const query = {
    field: params.field || '',
    techMaturity: params.techMaturity || '',
    transformMethod: params.transformMethod || '',
    achievementName: params.achievementName || '',
    pageNum,
    page: pageNum,
    pageSize: params.pageSize || 10
  }
  if (params.firstCategory) query.firstCategory = params.firstCategory
  if (params.secondCategory) query.secondCategory = params.secondCategory
  // 人才详情等场景：按创建人过滤
  if (params.createById != null && params.createById !== '') {
    query.createById = String(params.createById)
  }
  return request({
    url: '/open/achievement/supermarket-list',
    method: 'get',
    params: query,
    baseURL: baseUrl.achievement,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 需求大厅接口
 * @param {Object} params 查询参数
 * @param {String} params.firstCategory 一级类别
 * @param {String} params.secondCategory 二级类别
 * @param {Number} params.num 条数
 * @returns {Promise} 返回需求列表数据
 */
supplyDemandApi.getRequirementHall = function(params = {}) {
  const query = {
    num: params.num || 8
  }
  if (params.firstCategory) query.firstCategory = params.firstCategory
  if (params.secondCategory) query.secondCategory = params.secondCategory
  return request({
    url: '/open/requirement/hall',
    method: 'get',
    params: query,
    baseURL: baseUrl.requirement,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 成果详情接口（成果超市）
 * @param {String} id 成果ID
 * @returns {Promise} 详情数据
 */
supplyDemandApi.getAchievementDetail = function(id) {
  return request({
    url: `/open/achievement/supermarket-detail/${id}`,
    method: 'get',
    baseURL: baseUrl.achievement,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 类似成果推荐
 * @param {Number} num 所需条数
 * @param {String|Number} [id] 当前成果详情 ID
 * @returns {Promise} 推荐成果列表
 */
supplyDemandApi.getAchievementRecommend = function(num = 4, id) {
  const params = { num }
  if (id != null && id !== '') params.id = id
  return request({
    url: '/open/achievement/recommend',
    method: 'get',
    params,
    baseURL: baseUrl.achievement,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 需求大厅分页列表接口
 * @param {Object} params 查询参数
 * @param {String} params.firstCategory 一级类别
 * @param {String} params.secondCategory 二级类别
 * @param {String} params.field 领域
 * @param {String} params.cooperationMethod 合作方式
 * @param {String} [params.createById] 需求创建人ID
 * @param {Number} params.page 页数
 * @param {Number} params.pageSize 条数
 * @returns {Promise} 返回需求列表数据
 */
supplyDemandApi.getRequirementPageList = function(params = {}) {
  const pageNum = params.pageNum || params.page || 1
  const query = {
    field: params.field || '',
    cooperationMethod: params.cooperationMethod || '',
    // 模糊搜索：需求名称
    requirementName: params.requirementName || '',
    // 统一前端使用 pageNum；兼容后端使用 page
    pageNum,
    page: pageNum,
    pageSize: params.pageSize || 16,
    recruit: params.recruit || undefined
  }
  if (params.firstCategory) query.firstCategory = params.firstCategory
  if (params.secondCategory) query.secondCategory = params.secondCategory
  // 人才详情等场景：按创建人过滤
  if (params.createById != null && params.createById !== '') {
    query.createById = String(params.createById)
  }
  return request({
    url: '/open/requirement/page-requirement-list',
    method: 'get',
    params: query,
    baseURL: baseUrl.requirement,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 已转化成果列表（人才详情）
 * @deprecated 请使用 techTalentApi.getConvertedTecList
 */
supplyDemandApi.getConvertedAchievementList = function(params = {}) {
  const id = params.id || params.createById || params.userId || ''
  return request({
    url: '/portal/match/getTecList',
    method: 'get',
    params: { id },
    baseURL: baseUrl.matcher,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 需求详情接口
 * @param {String} id 需求ID
 * @returns {Promise} 详情数据
 */
supplyDemandApi.getRequirementDetail = function(id) {
  return request({
    url: `/open/requirement/${id}`,
    method: 'get',
    baseURL: baseUrl.requirement,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 需求揭榜进度查询
 * @param {String} id 外门户需求编号（归档需求ID）
 * @returns {Promise} 状态数据
 */
supplyDemandApi.getRequirementStatus = function(id) {
  return request({
    url: `/open/requirement/status/${id}`,
    method: 'get',
    baseURL: baseUrl.requirement,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 类似需求推荐
 * @param {Number} num 所需条数
 * @param {String|Number} [id] 当前需求详情 ID
 * @returns {Promise} 推荐需求列表
 */
supplyDemandApi.getRequirementRecommend = function(num = 4, id) {
  const params = { num }
  if (id != null && id !== '') params.id = id
  return request({
    url: '/open/requirement/recommend',
    method: 'get',
    params,
    baseURL: baseUrl.requirement,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 通过需求名称搜索需求列表
 * @param {String} requirementName 需求名称
 * @returns {Promise} 需求列表
 */
supplyDemandApi.getRequirementListByName = function(requirementName) {
  return request({
    url: '/requirement-api/requirement/list-by-requirement-name',
    method: 'get',
    params: {
      requirementName
    },
    baseURL: baseUrl.apiBaseAuth,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 获取我的需求列表
 * @param {Number} pageNum 页码
 * @param {Number} pageSize 每页数量
 * @param {String} requirementName 需求名称（可选）
 * @returns {Promise} 我的需求列表
 */
supplyDemandApi.getMyRequirementList = function(pageNum = 1, pageSize = 20, requirementName = '') {
  return request({
    url: '/requirement-api/requirement/external-portal/my',
    method: 'get',
    params: {
      pageNum,
      pageSize,
      requirementName
    },
    baseURL: baseUrl.apiBaseAuth,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 获取我的成果列表
 * @param {Number} pageNum 页码
 * @param {Number} pageSize 每页数量
 * @param {String} achievementName 成果名称（可选）
 * @returns {Promise} 我的成果列表
 */
supplyDemandApi.getMyAchievementList = function(pageNum = 1, pageSize = 20, achievementName = '') {
  return request({
    url: '/achievement-api/achievement/my',
    method: 'get',
    params: {
      pageNum,
      pageSize,
      achievementName
    },
    baseURL: baseUrl.apiBaseAuth,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 保存揭榜信息
 * @param {Object} data 提交数据
 * @param {String} data.id 揭榜id（新增传空字符串）
 * @param {String} data.requirementId 原始需求编号
 * @param {String} data.companyName 揭榜单位
 * @param {String} data.contactPerson 联系人
 * @param {String} data.contactPhone 联系方式
 * @param {String} data.solution 解决方案思路
 * @param {String} data.companyIntro 揭榜单位简介
 * @returns {Promise}
 */
supplyDemandApi.saveRequirementRecruit = function(data = {}) {
  return request({
    url: '/requirement-api/requirement-recruit/req-save',
    method: 'post',
    data: {
      id: data.id || '',
      requirementId: data.requirementId || '',
      companyName: data.companyName || '',
      contactPerson: data.contactPerson || '',
      contactPhone: data.contactPhone || '',
      solution: data.solution || '',
      companyIntro: data.companyIntro || ''
    },
    baseURL: baseUrl.apiBaseAuth,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 获取用户需求揭榜信息（登录）
 * @param {String} id 原始需求编号
 * @returns {Promise}
 */
supplyDemandApi.getRequirementRecruitDetail = function(id) {
  return request({
    url: `/requirement-api/requirement-recruit/req-detail/${id}`,
    method: 'get',
    baseURL: baseUrl.apiBaseAuth,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 获取需求总数量
 * @returns {Promise} 需求数量
 */
supplyDemandApi.getRequirementNum = function() {
  return request({
    url: '/open/requirement/num',
    method: 'get',
    baseURL: baseUrl.requirement,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 获取成果总数量
 * @returns {Promise} 成果数量
 */
supplyDemandApi.getAchievementNum = function() {
  return request({
    url: '/open/achievement/num',
    method: 'get',
    baseURL: baseUrl.achievement,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

export default supplyDemandApi
